<template>
  <div id="search-filters" class="search-filters w-100">
    <b-container fluid>
      <b-row>
        <b-col>
          <b-row
            :class="
              isMobile
                ? 'd-flex flex-column align-items-center justify-content-center px-3'
                : 'filter-row'
            "
          >
            <template v-if="isMobile">
              <p class="font-bold font-xl mb-2 w-100 filter-wrapper">Features</p>
              <InstantMatchesFilter class="mb-3" />
              <InstantLeaseFilter class="mb-3" />
            </template>
            <div
              v-for="(filter, index) in filters"
              :key="filter.name + 'filterIndex'"
              class="filter-wrapper"
            >
              <div v-if="!filter.hidden" :key="filter.name + index" class="filter-params">
                <b-button
                  :class="[
                    'd-none',
                    { 'd-inline': !isMobile, active: filterIsActive(filter.queryKeys) },
                  ]"
                  :pressed.sync="filter.state"
                  variant="filter"
                  v-if="!isMobile"
                >
                  {{ filter.name }}
                  <img :src="getIconUrl('icon-caret-down.svg')" />
                </b-button>
                <p class="font-bold font-xl mb-2" v-if="isMobile">{{ filter.name }}</p>
                <div class="filter-phantom" @click="filter.state = false" v-if="filter.state"></div>
                <!-- Slider filters (# of rooms, price range) -->
                <div
                  class="filter-component"
                  :class="{ isMobile: isMobile }"
                  v-if="(filter.state || isMobile) && filter.type === 'range'"
                >
                  {{ filter.settings }}
                  <vue-slider
                    v-model="filter.value"
                    :width="250"
                    :interval="filter.interval"
                    :min="filter.min"
                    :max="filter.max"
                    @drag-end="filter.callbackFn"
                    @click.native="filter.callbackFn"
                    class="range-slider"
                    :tooltipFormatter="filter.formatter"
                    :processStyle="{ backgroundColor: '#24201F' }"
                    :dotStyle="{
                      backgroundColor: '#24201F',
                      borderColor: '#24201F',
                    }"
                    :railStyle="{
                      'background-color': 'rgb(219, 217, 216)',
                      height: '1.5px',
                    }"
                    tooltip="always"
                  ></vue-slider>
                </div>

                <!-- Date filters -->
                <div
                  class="filter-component date-picker"
                  v-if="(filter.state || isMobile) && filter.type === 'date'"
                >
                  {{ filter.settings }}
                  <datepicker
                    v-model="filter.value"
                    minimumView="day"
                    maximumView="day"
                    initialView="day"
                    :inline="true"
                    calendar-class="cust-cal"
                    :disabled-dates="disabledDates"
                    @selected="filter.callbackFn"
                    :disabled="!!filter.disabled"
                  />
                </div>

                <!-- More Filters (checkboxes) -->
                <div
                  class="filter-component check check-group"
                  v-if="(filter.state || isMobile) && filter.type === 'check'"
                >
                  <b-form-group>
                    <b-form-checkbox
                      class="filter-checkbox"
                      v-for="(item, key, index) in filter.value"
                      :key="`filterCheckbox-${key}-${index}`"
                      v-model="filter.value[key]"
                      :value="true"
                      @input="(val) => filter.callbackFn(key, val)"
                    >
                      {{ key }}
                    </b-form-checkbox>
                  </b-form-group>
                </div>
              </div>
            </div>
            <template v-if="!isMobile">
              <InstantMatchesFilter class="mr-2" />
              <InstantLeaseFilter />
            </template>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import moment from 'moment'
import { camelCase } from 'lodash-es'
import { subDays } from 'date-fns'
import { mapActions, mapGetters } from 'vuex'
import VueSlider from 'vue-slider-component'
import Datepicker from 'vuejs-datepicker'
import InstantMatchesFilter from '@/components/Listings/InstantMatchesFilter.vue'
import InstantLeaseFilter from '@/components/Listings/InstantLeaseFilter.vue'

export default {
  name: 'ListingsFilters',
  components: {
    VueSlider,
    Datepicker,
    InstantMatchesFilter,
    InstantLeaseFilter,
  },
  props: {
    isMobile: {
      type: Boolean,
    },
    // by passing this function to this component, the route will no longer be automatically
    // updated with the query parameters, you will instead be passed the updated query values
    // to do with what you want
    onFilterUpdate: {
      type: Function,
      default: null,
    },
    homeType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      filters: {
        price: {
          name: 'Price Range',
          state: false,
          type: 'range',
          queryKeys: ['priceMin', 'priceMax'],
          callbackFn: this.updatePrice,
          value: [
            this.$route.query.priceMin > 0
              ? this.$route.query.priceMin
              : this.homeType === 'co_living'
              ? 500
              : 1000,
            this.$route.query.priceMax > 0
              ? this.$route.query.priceMax
              : this.homeType === 'co_living'
              ? 3000
              : 8000,
          ],
          formatter: (v) => `$${v}`,
          min: 0,
          max: this.homeType === 'co_living' ? 3500 : 10000,
          interval: 10,
        },
        moveInDate: {
          name: 'Move-in Date',
          state: false,
          type: 'date',
          queryKeys: ['moveInDate'],
          callbackFn: this.updateDate,
          value: this.$route.query.moveInDate ? this.$route.query.moveInDate : null,
          disabled: {
            to: new Date(),
          },
        },
        availableRooms: {
          name: 'Available Rooms',
          state: false,
          type: 'range',
          callbackFn: this.updateAvailableRooms,
          queryKeys: ['availableRoomsMin', 'availableRoomsMax'],
          value: [
            this.$route.query.availableRoomsMin > 1 ? this.$route.query.availableRoomsMin : 1,
            this.$route.query.availableRoomsMax > 1 ? this.$route.query.availableRoomsMax : 10,
          ],
          min: 1,
          max: 10,
          interval: 1,
        },
        moreFilters: {
          name: 'More Filters',
          state: false,
          type: 'check',
          queryKeys: [
            'masterBedroom',
            'privateBathroom',
            'petFriendly',
            'washer',
            'dryer',
            'backyard',
            'pool',
          ],
          callbackFn: this.updateMoreFilters,
          value: {
            'Master Bedroom': this.$route.query.masterBedroom
              ? this.$route.query.masterBedroom
              : null,
            'Private Bathroom': this.$route.query.privateBathroom
              ? this.$route.query.privateBathroom
              : null,
            'Pet Friendly': this.$route.query.petFriendly ? this.$route.query.petFriendly : null,
            Washer: this.$route.query.washer ? this.$route.query.washer : null,
            Dryer: this.$route.query.dryer ? this.$route.query.dryer : null,
            Backyard: this.$route.query.backyard ? this.$route.query.backyard : null,
            Pool: this.$route.query.pool ? this.$route.query.pool : null,
          },
        },
      },
    }
  },
  watch: {
    homeType: {
      handler(val) {
        if (val === 'group_living') {
          this.filters.availableRooms.name = 'Bedrooms'
          delete this.filters.roommateInterests
          delete this.filters.moreFilters.value['Master Bedroom']
          delete this.filters.moreFilters.value['Private Bathroom']
        } else {
          this.filters.availableRooms.name = 'Available Rooms'
          this.filters.moreFilters.value = {
            'Master Bedroom': this.$route.query.masterBedroom
              ? this.$route.query.masterBedroom
              : null,
            'Private Bathroom': this.$route.query.privateBathroom
              ? this.$route.query.privateBathroom
              : null,
            ...this.filters.moreFilters.value,
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapGetters('interests', ['interests']),
    disabledDates() {
      return {
        to: subDays(new Date(), 1),
      }
    },
  },
  methods: {
    ...mapActions('interests', ['fetchInterests']),
    getIconUrl(icon) {
      return require('@/assets/icons/' + icon)
    },
    filterIsActive(keys = []) {
      return keys.some((key) => Object.prototype.hasOwnProperty.call(this.$route.query, key))
    },
    updatePrice() {
      const { filters, $route, $router } = this
      const { market } = $route.query

      // Update router query params via extend, but do not change history
      const newOptions = { ...$route.query }

      newOptions.priceMin = filters.price.value[0]
      newOptions.priceMax = filters.price.value[1]

      this.onFilterUpdate
        ? this.onFilterUpdate(newOptions)
        : $router.replace({ query: newOptions }).catch(() => {})

      if (newOptions.priceMin >= 0 && newOptions.priceMax >= 0) {
        this.$segment.track('Listings Filters Updated', {
          type: 'price',
          priceMin: newOptions.priceMin,
          priceMax: newOptions.priceMax,
          market,
        })
      }
    },
    updateDate() {
      // Wait for date model update!
      this.$nextTick(function () {
        const { filters, $route, $router } = this
        const { query } = $route

        // transform date format
        this.filters.moveInDate.value = moment(filters.moveInDate.value).format()

        // Update router query params via extend, but do not change history. Only if future date.
        const newOptions = {
          ...query,
          moveInDate: filters.moveInDate.value,
        }
        this.onFilterUpdate
          ? this.onFilterUpdate(newOptions)
          : $router.replace({ query: newOptions }).catch(() => {})

        if (newOptions.moveInDate) {
          this.$segment.track('Listings Filters Updated', {
            type: 'date',
            moveInDate: newOptions.moveInDate,
            city: query.market,
          })
        }
      })
    },
    updateAvailableRooms() {
      const { filters, $route, $router } = this
      const { query } = $route

      // Update router query params via extend, but do not change history
      const newOptions = {
        ...query,
        availableRoomsMin: filters.availableRooms.value[0],
        availableRoomsMax: filters.availableRooms.value[1],
      }
      this.onFilterUpdate
        ? this.onFilterUpdate(newOptions)
        : $router.replace({ query: newOptions }).catch(() => {})

      if (newOptions.availableRoomsMin >= 1 && newOptions.availableRoomsMax >= 1) {
        this.$segment.track('Listings Filters Updated', {
          type: 'available_rooms',
          availableRoomsMin: newOptions.availableRoomsMin,
          availableRoomsMax: newOptions.availableRoomsMax,
          city: query.market,
        })
      }
    },
    updateMoreFilters(filterName, filterValue) {
      // Camel case the filter key for compatibility and readibility:
      const filterKey = camelCase(filterName)

      // Update the state of the filters with change(s):
      this.filters.moreFilters.value[filterName] = filterValue

      // Build new query params object:
      const updatedFilterQuery = {
        ...this.$route.query,
        [filterKey]: (filterValue && filterValue) || undefined,
      }
      this.onFilterUpdate
        ? this.onFilterUpdate(updatedFilterQuery)
        : this.$router.replace({ query: updatedFilterQuery }).catch(() => {})

      // Analytics
      this.$segment.track('Listings Filters Updated', {
        type: 'more_filters',
        [filterKey]: filterValue,
        city: this.$route.query.market,
      })
    },
  },
}
</script>

<style lang="scss">
#search-filters {
  position: relative;
  z-index: 4;
  background: white;

  @include desktop {
    margin-bottom: 20px;
  }

  .market-select {
    @media (min-width: 1200px) {
      border-right: $border-default;
      padding-right: 1rem;
      margin-right: 1rem;
    }

    @media (max-width: 767px) {
      height: 4rem;
      border-bottom: $border-default;
      margin-bottom: 3rem;
    }
  }

  .new-listing-wrapper {
    max-height: 60px;
  }

  .btn-filter {
    color: $charcoal-60 !important;

    &:hover {
      border-color: $charcoal-40;
    }
  }

  .filter-component.check.check-group {
    max-height: 400px;
    overflow: scroll;

    @include mobile {
      max-height: initial;
    }
  }

  img {
    height: 15px;
    width: 15px;
    margin-left: 8px;

    &.waitlist {
      width: 20px;
    }
  }

  .filter-wrapper {
    display: inline-block;

    margin-bottom: 10px;

    @include mobile {
      margin-bottom: 0;
    }

    &.clear-filter-wrapper {
      button {
        font-weight: bold;
      }
      img {
        height: 12px;
        width: 12px;
        margin-left: 8px;
        margin-bottom: 1px;
      }
    }

    .filter-phantom {
      position: fixed;
      top: 0px;
      left: 0px;
      height: 100%;
      width: 100%;
      z-index: 9;
    }

    .filter-component {
      top: 100%;
      position: absolute;
      z-index: 10;
      max-width: 310px;
      width: 100%;
      height: fit-content;
      min-height: 5em;
      padding: 0 20px;
      background: white;
      border-radius: 0px;
      border: $border-default;
      box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);

      &.date-picker {
        max-width: 280px;
        padding: 0;
      }

      &.check-group {
        min-height: 7.5em;
        @media (min-width: 768px) {
          max-width: 14rem;
        }
      }

      .range-slider {
        margin: 3.5rem auto 2rem auto;
        position: relative;
        display: block;

        .vue-slider {
          height: 2px !important;
        }
      }

      .vue-slider-dot {
        width: 20px !important;
        height: 20px !important;
        border: 2px solid $charcoal-60 !important;
        border-radius: 100%;
        background: white;
      }

      .vue-slider-dot-tooltip-inner {
        top: -1px;
        position: relative;
        font-weight: bold;
        display: block;
        font-size: 14px;
        white-space: nowrap;
        padding: 2px 5px;
        min-width: 20px;
        text-align: center;
        color: #fff;
        border-radius: 5px;
        position: relative;
        font-weight: 700;
        background-color: rgb(36, 32, 31);
        border-color: rgb(36, 32, 31);
        &:before {
          content: '';
          position: absolute;
          bottom: -10px;
          left: 50%;
          width: 0;
          height: 0;
          border: 5px solid transparent;
          border-top-color: inherit;
          transform: translate(-50%);
        }
      }

      .custom-control-description {
        margin-top: 3px;
      }

      &.check {
        padding: 15px;

        .custom-control {
          cursor: pointer;
          padding: 0.25rem 0;
          padding-left: 2.5rem;
          margin-bottom: 0;
          color: $charcoal-80;
          font-weight: $font-normal;

          label {
            height: 100%;
            border-radius: 4px;
            width: 100%;
            cursor: pointer;
            padding-left: 0.25rem;

            &:hover {
              font-weight: bold;
            }
          }
        }

        .custom-control-indicator {
          &:before {
            content: ' ';
            width: 22px;
            height: 22px;
            left: 0;
            top: 1px;
            background: white;
            border: 1px solid $charcoal-40;
            position: absolute;
            border-radius: 0px;
          }
        }
        .checked {
          .custom-control-indicator {
            &:before {
              transition: content 0.25s ease;
              padding-top: 2px;
              width: 22px;
              height: 22px;
              border: $border-default;
              border-color: $blue-60;
              background: $blue-60;
              content: url('../../assets/icons/icon-check.svg');
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        }
      }
    }
  }
  @media (max-width: 767px) {
    .filter-wrapper {
      display: block;
      width: 100%;
      max-width: 280px;
      padding-right: 10px;

      .filter-component {
        display: block;
        position: relative;
        box-shadow: none;
        border-radius: 0;
        border: 0;
        min-height: 0;
        border-bottom: $border-default;
        margin-top: 0.5em;
        margin-bottom: 2em;
        max-width: 100%;
        .range-slider {
          padding: 0;
        }
      }
    }
  }
}
@include desktop {
  .filter-params {
    button {
      border-radius: 4px;
      margin-right: 8px;
      border: 1px solid #e6e3e3;
      color: $charcoal-30 !important;
      font-weight: bold;
      height: 48px;
      min-width: 100px;
      padding: 0 0.75rem !important;
      display: flex !important;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      img {
        display: none;
      }
      &.active {
        border: 1px solid black;
        color: black !important;
      }
    }
  }
}
</style>
